<template>
  <v-card flat class="pa-3 mt-1">
    <v-card-title class="d-flex align-center justify-center py-0">
      <v-img :src="require('@/assets/images/logos/logo.png')" max-width="300px" alt="logo" contain></v-img>
    </v-card-title>

    <v-form class="multi-col-validation">
      <v-card-text class="pt-1">
        <p class="text-h6 text-center font-weight-bold">Make a Contribution</p>
        <input type="hidden" v-model="payment_ref" ref="payment_ref" id="payment_ref" />

        <!-- Sélection membre et groupe -->
        <v-row>
          <v-col cols="10" class="py-0 my-1">
            <v-text-field
              v-model="userName"
              outlined dense
              label="Search username, phone number..."
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="getUsers"
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="py-0 my-1">
            <v-btn color="primary" fab small @click="getUsers" :loading="searchingUsers">
              <v-icon>mdi-account-search</v-icon>
            </v-btn>
          </v-col>

          <v-col cols="12" class="py-0 my-1">
            <v-select
              v-model="selectedUser"
              :items="users"
              item-text="username"
              item-value="id"
              outlined dense
              label="Select a user"
              @change="getUserDetails"
            ></v-select>
          </v-col>

          <v-col cols="12" class="py-0 my-1">
            <v-select
              v-model="selectedNjangi"
              :items="njangi_groups"
              item-text="njangi_group_name"
              item-value="id"
              outlined dense
              label="Select Njangi Group"
              @change="getContributionSummary"
            ></v-select>
          </v-col>

          <v-col cols="12" class="py-0 my-1">
            <v-text-field
              v-model="currentPassword"
              :type="isCurrentPasswordVisible ? 'text' : 'password'"
              :append-icon="isCurrentPasswordVisible ? mdiEyeOffOutline : mdiEyeOutline"
              label="Current Password"
              outlined dense
              @click:append="isCurrentPasswordVisible = !isCurrentPasswordVisible"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <PaymentLoader :visible="loadingSummary" message="Loading contribution summary" />

      <!-- Résumé -->
      <v-card-text v-if="contributionSummary" class="pt-0">
        <v-divider class="mb-4"></v-divider>

        <!-- Statut du membre -->
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="summary-section-label">
            <v-icon small class="me-1">mdi-account-check</v-icon> Member Status
          </div>
          <v-chip :color="statusColor" text-color="white" small>
            {{ statusLabel }}
          </v-chip>
        </div>

        <!-- Arriérés -->
        <template v-if="contributionSummary.has_penalties">
          <div class="summary-banner error-banner mb-3">
            <v-icon small class="me-2" color="error">mdi-alert-outline</v-icon>
            <span>This member has unpaid penalties and must settle them before paying current or next cycles.</span>
          </div>

          <div class="summary-section-label mb-2">Payment options for arrears</div>
          <v-radio-group v-model="paymentOption" row class="mt-0 mb-1">
            <v-radio label="Pay all due now" value="all_due"></v-radio>
            <v-radio label="Pay selected cycle" value="normal"></v-radio>
          </v-radio-group>

          <template v-if="paymentOption === 'normal'">
            <v-select
              v-model="selectedCycleId"
              :items="cycleOptions"
              item-text="text"
              item-value="value"
              label="Choose a cycle to pay"
              outlined dense
              class="mb-1"
            ></v-select>
            <div v-if="selectedCycle" class="cycle-preview mb-3">
              <strong>{{ selectedCycle.title }}</strong> — {{ selectedCycle.period }}
              <div>Cycle total: <strong>{{ formatAmount(selectedCycle.missed_amount + selectedCycle.penalty_amount) }}</strong></div>
            </div>
          </template>

          <!-- Liste des cycles manqués -->
          <div class="mt-2">
            <div class="summary-section-label mb-2">Missed cycles breakdown</div>
            <v-row dense>
              <v-col v-for="cycle in displayedCycles" :key="cycle.id" cols="12" md="6">
                <div class="cycle-card mb-2">
                  <div class="cycle-card-header">
                    <span class="cycle-card-title">{{ cycle.title }}</span>
                    <span class="cycle-period-tag">{{ cycle.period }}</span>
                  </div>
                  <div class="cycle-card-metrics">
                    <div>
                      <div class="cycle-metric-label">Missed</div>
                      <div class="cycle-metric-value error--text">{{ formatAmount(cycle.missed_amount) }}</div>
                    </div>
                    <div>
                      <div class="cycle-metric-label">Penalty</div>
                      <div class="cycle-metric-value warning--text">{{ formatAmount(cycle.penalty_amount) }}</div>
                    </div>
                    <div>
                      <div class="cycle-metric-label">Total</div>
                      <div class="cycle-metric-value primary--text">{{ formatAmount(cycle.total_amount) }}</div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <div class="text-center mt-2">
              <v-btn v-if="contributionSummary.missed_cycles.length > 5" small text color="primary" @click="showAllCycles = !showAllCycles">
                {{ showAllCycles ? 'See less' : `See more (${contributionSummary.missed_cycles.length - 5} more)` }}
              </v-btn>
            </div>
          </div>
        </template>

        <!-- Pas de pénalités : cycle actuel et prochain -->
        <template v-else>
          <div class="summary-section-label mb-3">Upcoming & Current Cycles</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="cycle-action-card" :class="{ 'paid-card': contributionSummary.current_cycle.is_paid }">
                <div class="pa-4">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="action-title">Current Cycle</div>
                      <div class="action-subtitle">
                        {{ contributionSummary.current_cycle.start }} → {{ contributionSummary.current_cycle.end }}
                      </div>
                    </div>
                    <v-icon size="32" :color="contributionSummary.current_cycle.is_paid ? 'success' : 'primary'">
                      {{ contributionSummary.current_cycle.is_paid ? 'mdi-check-circle' : 'mdi-calendar-check' }}
                    </v-icon>
                  </div>
                  <div class="d-flex align-center justify-space-between mt-3">
                    <span class="amount-label">Amount: {{ formatAmount(contributionSummary.normal_contribution) }}</span>
                    <v-btn
                      v-if="!contributionSummary.current_cycle.is_paid"
                      small
                      color="primary"
                      @click="selectCycle('current')"
                    >Pay</v-btn>
                    <v-chip v-else small color="success" text-color="white">Already Paid</v-chip>
                  </div>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="cycle-action-card" :class="{ 'paid-card': contributionSummary.next_cycle.is_paid }">
                <div class="pa-4">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="action-title">Next Cycle</div>
                      <div class="action-subtitle">
                        {{ contributionSummary.next_cycle.start }} → {{ contributionSummary.next_cycle.end }}
                      </div>
                    </div>
                    <v-icon size="32" :color="contributionSummary.next_cycle.is_paid ? 'success' : 'teal'">
                      {{ contributionSummary.next_cycle.is_paid ? 'mdi-check-circle' : 'mdi-calendar-plus' }}
                    </v-icon>
                  </div>
                  <div class="d-flex align-center justify-space-between mt-3">
                    <span class="amount-label">Amount: {{ formatAmount(contributionSummary.normal_contribution) }}</span>
                    <v-btn
                      v-if="!contributionSummary.next_cycle.is_paid"
                      small
                      color="teal"
                      @click="selectCycle('next')"
                    >Pay</v-btn>
                    <v-chip v-else small color="success" text-color="white">Already Paid</v-chip>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Bouton More cycles avec badge -->
          <div class="text-center mt-4">
            <v-btn outlined color="primary" @click="showUpcomingCyclesDialog = true">
              <v-icon left>mdi-calendar-month</v-icon>
              More Upcoming Cycles
              <span v-if="unpaidUpcomingCount > 0" class="ml-2">
                <v-chip x-small color="warning" text-color="white">{{ unpaidUpcomingCount }}</v-chip>
              </span>
            </v-btn>
          </div>

          <!-- Popup amélioré des cycles à venir -->
          <v-dialog v-model="showUpcomingCyclesDialog" max-width="850px" persistent>
            <v-card class="upcoming-dialog">
              <v-card-title class="headline primary--text d-flex align-center py-4">
                <v-icon left color="primary">mdi-calendar-star</v-icon>
                Upcoming Cycles
                <v-spacer></v-spacer>
                <v-btn icon @click="showUpcomingCyclesDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text class="pa-0">
                <!-- En-tête du résumé -->
                <div class="upcoming-summary-banner px-4 py-3">
                  <div class="d-flex align-center justify-space-between flex-wrap gap-2">
                    <div>
                      <div class="subtitle-2 grey--text">Total upcoming cycles</div>
                      <div class="font-weight-bold">{{ contributionSummary.upcoming_cycles.length }}</div>
                    </div>
                    <div>
                      <div class="subtitle-2 grey--text">Unpaid</div>
                      <div class="font-weight-bold warning--text">{{ unpaidUpcomingCount }}</div>
                    </div>
                    <div>
                      <div class="subtitle-2 grey--text">Amount per cycle</div>
                      <div class="font-weight-bold primary--text">{{ formatAmount(contributionSummary.normal_contribution) }} F CFA</div>
                    </div>
                  </div>
                </div>

                <!-- Barre de recherche et filtre -->
                <div class="pa-4">
                  <v-row dense>
                    <v-col cols="12" md="7">
                      <v-text-field
                        v-model="searchUpcoming"
                        outlined dense
                        label="Search cycles"
                        prepend-inner-icon="mdi-magnify"
                        clearable
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="5">
                      <v-select
                        v-model="filterUpcoming"
                        outlined dense
                        label="Filter by status"
                        :items="filterOptions"
                        item-text="text"
                        item-value="value"
                      ></v-select>
                    </v-col>
                  </v-row>
                </div>

                <!-- Timeline des cycles -->
                <div class="upcoming-timeline px-4 pb-4">
                  <div
                    v-for="(cycle, index) in filteredUpcomingCycles"
                    :key="cycle.cycle_key"
                    class="timeline-item"
                  >
                    <div class="timeline-dot" :class="cycle.is_paid ? 'dot-paid' : 'dot-unpaid'"></div>
                    <div class="timeline-line" v-if="index < filteredUpcomingCycles.length - 1"></div>

                    <v-card outlined :class="{ 'paid-cycle': cycle.is_paid }" class="timeline-card mb-3">
                      <v-card-text class="pa-3">
                        <v-layout row wrap align-center>
                          <v-flex xs12 sm5>
                            <div class="d-flex align-center">
                              <v-icon :color="cycle.is_paid ? 'success' : 'primary'" class="me-2">
                                {{ cycle.is_paid ? 'mdi-check-circle' : 'mdi-calendar-clock' }}
                              </v-icon>
                              <div>
                                <div class="font-weight-medium">{{ cycle.cycle_key }}</div>
                                <div class="caption grey--text">{{ cycle.start }} → {{ cycle.end }}</div>
                              </div>
                            </div>
                          </v-flex>
                          <v-flex xs12 sm3 class="text-center">
                            <span class="font-weight-bold primary--text">{{ formatAmount(cycle.amount) }} F CFA</span>
                          </v-flex>
                          <v-flex xs12 sm2 class="text-center">
                            <v-chip :color="cycle.is_paid ? 'success' : 'warning'" small text-color="white">
                              {{ cycle.is_paid ? 'Paid' : 'Unpaid' }}
                            </v-chip>
                          </v-flex>
                          <v-flex xs12 sm2 class="text-right">
                            <v-btn
                              v-if="!cycle.is_paid"
                              small
                              color="primary"
                              @click="paySpecificCycle(cycle)"
                            >
                              <v-icon left small>mdi-cash</v-icon> Pay
                            </v-btn>
                            <v-btn v-else small disabled>Paid</v-btn>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div v-if="filteredUpcomingCycles.length === 0" class="text-center py-6">
                    <v-icon size="48" color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
                    <div class="grey--text mt-2">No cycles match your search.</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-dialog>
        </template>
      </v-card-text>

      <!-- Bouton principal contextuel -->
      <v-card-text class="text-end">
        <spinner v-if="loading" class="text-end"></spinner>
        <v-btn
          v-else
          color="primary"
          class="me-3 mt-3"
          :disabled="!canSubmit || (contributionSummary && contributionSummary.has_penalties ? false : selectedCycleType === null)"
          @click="confirmPayment"
        >
          <v-icon left small>mdi-send</v-icon>
          {{ submitButtonLabel }}
        </v-btn>
      </v-card-text>
    </v-form>

    <PaymentLoader :visible="loading" message="Processing payment" />

    <!-- Modale de confirmation -->
    <v-dialog v-model="confirmDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline primary--text">Confirm Payment</v-card-title>
        <v-card-text>
          <div v-if="confirmDetails">
            <p class="mb-1"><strong>Member:</strong> {{ confirmDetails.memberName }}</p>
            <p class="mb-1"><strong>Group:</strong> {{ confirmDetails.groupName }}</p>
            <p class="mb-1"><strong>Amount:</strong> {{ formatAmount(confirmDetails.amount) }} F CFA</p>
            <p class="mb-1"><strong>Type:</strong> {{ confirmDetails.typeLabel }}</p>
            <p v-if="confirmDetails.period" class="mb-1"><strong>Period:</strong> {{ confirmDetails.period }}</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="proceedPayment">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from 'axios'
import Spinner from '@/components/Spinner.vue'
import PaymentLoader from '@/components/PaymentLoader.vue'
import { mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js'

export default {
  components: { Spinner, PaymentLoader },
  data() {
    return {
      loading: false,
      loadingSummary: false,
      searchingUsers: false,
      userName: '',
      selectedUser: '',
      selectedNjangi: null,
      isCurrentPasswordVisible: false,
      currentPassword: '',
      payment_ref: '',
      users: [],
      njangi_groups: [],
      contributionSummary: null,
      selectedCycleId: null,
      paymentOption: 'all_due',
      showAllCycles: false,
      selectedCycleType: null,
      showUpcomingCyclesDialog: false,
      confirmDialog: false,
      confirmDetails: null,
      pendingPayment: null,
      searchUpcoming: '',
      filterUpcoming: 'all',
      mdiEyeOffOutline,
      mdiEyeOutline,
    }
  },
  computed: {
    selectedPaymentAmount() {
      if (!this.contributionSummary) return 0
      if (this.contributionSummary.has_penalties) {
        if (this.paymentOption === 'all_due') {
          return this.contributionSummary.missed_contributions + this.contributionSummary.pending_penalties
        }
        if (this.selectedCycle) {
          return this.selectedCycle.missed_amount + this.selectedCycle.penalty_amount
        }
        return 0
      }
      if (this.selectedCycleType === 'current' || this.selectedCycleType === 'next') {
        return this.contributionSummary.normal_contribution
      }
      return 0
    },
    totalDue() {
      return this.selectedPaymentAmount
    },
    selectedCycle() {
      if (!this.contributionSummary?.missed_cycles) return null
      return this.contributionSummary.missed_cycles.find(c => c.id === this.selectedCycleId) ||
             this.contributionSummary.missed_cycles[0]
    },
    cycleOptions() {
      if (!this.contributionSummary?.missed_cycles) return []
      return this.contributionSummary.missed_cycles.map(cycle => ({
        value: cycle.id,
        text: `${cycle.title} — ${cycle.period}`,
      }))
    },
    displayedCycles() {
      if (!this.contributionSummary?.missed_cycles) return []
      return this.showAllCycles ? this.contributionSummary.missed_cycles : this.contributionSummary.missed_cycles.slice(0, 5)
    },
    canSubmit() {
      return this.selectedUser && this.selectedNjangi && this.currentPassword && this.contributionSummary
    },
    paymentOptions() {
      return [
        { text: 'Pay all due now', value: 'all_due' },
        { text: 'Pay selected cycle', value: 'normal' },
      ]
    },
    filterOptions() {
      return [
        { text: 'All', value: 'all' },
        { text: 'Unpaid', value: 'unpaid' },
        { text: 'Paid', value: 'paid' },
      ]
    },
    filteredUpcomingCycles() {
      if (!this.contributionSummary?.upcoming_cycles) return []
      let cycles = this.contributionSummary.upcoming_cycles
      if (this.filterUpcoming === 'unpaid') {
        cycles = cycles.filter(c => !c.is_paid)
      } else if (this.filterUpcoming === 'paid') {
        cycles = cycles.filter(c => c.is_paid)
      }
      if (this.searchUpcoming) {
        const query = this.searchUpcoming.toLowerCase()
        cycles = cycles.filter(c =>
          c.cycle_key.toLowerCase().includes(query) ||
          c.start.toLowerCase().includes(query) ||
          c.end.toLowerCase().includes(query)
        )
      }
      return cycles
    },
    unpaidUpcomingCount() {
      if (!this.contributionSummary?.upcoming_cycles) return 0
      return this.contributionSummary.upcoming_cycles.filter(c => !c.is_paid).length
    },
    submitButtonLabel() {
      if (!this.contributionSummary) return ''
      if (this.contributionSummary.has_penalties) {
        return this.paymentOption === 'all_due' ? 'Pay All Due' : 'Pay Selected Cycle'
      }
      if (this.selectedCycleType === 'current') return 'Pay Current Cycle'
      if (this.selectedCycleType === 'next') return 'Pay Next Cycle'
      return 'Select a Cycle'
    },
    statusColor() {
      if (!this.contributionSummary) return 'grey'
      if (this.contributionSummary.has_penalties) return 'error'
      if (this.contributionSummary.current_cycle?.is_paid || this.contributionSummary.next_cycle?.is_paid) return 'success'
      return 'primary'
    },
    statusLabel() {
      if (!this.contributionSummary) return ''
      if (this.contributionSummary.has_penalties) return 'Has Arrears'
      if (this.contributionSummary.current_cycle?.is_paid) return 'Current Cycle Paid'
      if (this.contributionSummary.next_cycle?.is_paid) return 'Next Cycle Paid'
      return 'Up to Date'
    },
  },
  async mounted() {
    document.title = 'Manager: Njangi | Make Contribution'
    await this.getContributionPaymentRef()
    const q = this.$route.query
    if (q?.userId) {
      this.selectedUser = q.userId
      await this.getUserDetails()
      if (q.njangiId) {
        this.selectedNjangi = q.njangiId
        await this.getContributionSummary()
      }
    }
  },
  methods: {
    formatAmount(value) {
      if (value == null) return '—'
      return Number(value).toLocaleString('fr-CM') + ' F CFA'
    },
    async getUsers() {
      this.searchingUsers = true
      try {
        const response = await axios.get('/api/v1/manager/get/clients/' + this.userName + '/')
        this.users = response.data
      } catch (error) {
        this.showError(error)
      } finally {
        this.searchingUsers = false
      }
    },
    async getUserDetails() {
      if (!this.selectedUser) return
      try {
        const response = await axios.get('/api/v1/manager/njangi/get/client/information/' + this.selectedUser + '/')
        this.njangi_groups = response.data['njangi_groups']
      } catch (error) {
        this.showError(error)
      }
    },
    async getContributionPaymentRef() {
      try {
        const response = await axios.get('/api/v1/manager/njangi/create/user/contribute/group/payment_ref')
        this.payment_ref = response.data
      } catch (error) {
        this.showError(error)
      }
    },
    async getContributionSummary() {
      if (!this.selectedUser || !this.selectedNjangi) return
      this.loadingSummary = true
      try {
        const resp = await axios.get(
          `/api/v1/manager/njangi/get/client/contribution-summary/${this.selectedUser}/${this.selectedNjangi}/`
        )
        this.contributionSummary = resp.data
        if (this.contributionSummary.has_penalties) {
          this.paymentOption = 'all_due'
          this.selectedCycleType = 'arrears'
        } else {
          this.selectedCycleType = null
        }
        this.selectedCycleId = this.contributionSummary.missed_cycles?.[0]?.id || null
        this.showAllCycles = false
      } catch (error) {
        this.contributionSummary = null
        this.showError(error)
      } finally {
        this.loadingSummary = false
      }
    },
    selectCycle(type) {
      this.selectedCycleType = type
    },
    paySpecificCycle(cycle) {
      this.confirmDetails = {
        memberName: this.users.find(u => u.id === this.selectedUser)?.username || 'User',
        groupName: this.njangi_groups.find(g => g.id === this.selectedNjangi)?.njangi_group_name || 'Group',
        amount: cycle.amount,
        typeLabel: `Payment for cycle starting ${cycle.start}`,
        period: `${cycle.start} → ${cycle.end}`,
      }
      this.pendingPayment = {
        type: 'specific_future',
        cycleKey: cycle.cycle_key,
      }
      this.confirmDialog = true
    },
    confirmPayment() {
      if (!this.contributionSummary) return
      let typeLabel = ''
      let period = ''
      if (this.contributionSummary.has_penalties) {
        typeLabel = this.paymentOption === 'all_due' ? 'All dues (missed + penalties)' : 'Selected cycle payment'
        if (this.paymentOption === 'normal' && this.selectedCycle) {
          period = `${this.selectedCycle.title} (${this.selectedCycle.period})`
        }
      } else {
        if (this.selectedCycleType === 'current') {
          typeLabel = 'Current cycle payment'
          period = `${this.contributionSummary.current_cycle.start} → ${this.contributionSummary.current_cycle.end}`
        } else if (this.selectedCycleType === 'next') {
          typeLabel = 'Next cycle payment'
          period = `${this.contributionSummary.next_cycle.start} → ${this.contributionSummary.next_cycle.end}`
        }
      }
      this.confirmDetails = {
        memberName: this.users.find(u => u.id === this.selectedUser)?.username || 'User',
        groupName: this.njangi_groups.find(g => g.id === this.selectedNjangi)?.njangi_group_name || 'Group',
        amount: this.selectedPaymentAmount,
        typeLabel,
        period,
      }
      this.pendingPayment = null
      this.confirmDialog = true
    },
    async proceedPayment() {
      this.confirmDialog = false
      if (this.pendingPayment) {
        if (this.pendingPayment.type === 'specific_future') {
          const formData = new FormData()
          formData.append('njangi_group_id', this.selectedNjangi)
          formData.append('payment_ref', this.payment_ref)
          formData.append('user_id', this.selectedUser)
          formData.append('password', this.currentPassword)
          formData.append('cycle_key', this.pendingPayment.cycleKey)
          await this.postPayment('/api/v1/manager/njangi/pay_specific_future_cycle/', formData)
        }
        this.pendingPayment = null
        return
      }
      if (this.contributionSummary.has_penalties) {
        await this.submitArrearsPayment()
      } else {
        if (this.selectedCycleType === 'current') {
          await this.submitCurrentPayment()
        } else if (this.selectedCycleType === 'next') {
          await this.submitFuturePayment()
        }
      }
    },
    async submitArrearsPayment() {
      const formData = new FormData()
      formData.append('njangi_group_id', this.selectedNjangi)
      formData.append('payment_ref', this.payment_ref)
      formData.append('user_id', this.selectedUser)
      formData.append('password', this.currentPassword)
      formData.append('payment_option', this.paymentOption)
      formData.append('payment_amount', this.selectedPaymentAmount)
      if (this.paymentOption === 'normal' && this.selectedCycleId != null) {
        formData.append('selected_cycle_id', this.selectedCycleId)
      }
      await this.postPayment('/api/v1/manager/njangi/make_contribution/', formData)
    },
    async submitCurrentPayment() {
      const formData = new FormData()
      formData.append('njangi_group_id', this.selectedNjangi)
      formData.append('payment_ref', this.payment_ref)
      formData.append('user_id', this.selectedUser)
      formData.append('password', this.currentPassword)
      formData.append('payment_amount', this.contributionSummary.normal_contribution)
      formData.append('payment_option', 'normal')
      await this.postPayment('/api/v1/manager/njangi/make_contribution/', formData)
    },
    async submitFuturePayment() {
      const formData = new FormData()
      formData.append('njangi_group_id', this.selectedNjangi)
      formData.append('payment_ref', this.payment_ref)
      formData.append('user_id', this.selectedUser)
      formData.append('password', this.currentPassword)
      formData.append('payment_amount', this.contributionSummary.normal_contribution)
      await this.postPayment('/api/v1/manager/njangi/make_future_contribution/', formData)
    },
    async postPayment(url, formData) {
      this.loading = true
      try {
        const response = await axios.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.showSnack(response.data[0].text, response.data[0].color, 8000)
        if (response.data[0].category === 'success') {
          setTimeout(() => this.$router.push('/njangi/contribution_transactions'), 4000)
        }
      } catch (error) {
        this.showError(error)
      } finally {
        this.loading = false
      }
    },
    showError(error) {
      const msg = error.response ? `${error.response.status} ${error.response.statusText}` : 'An error occurred. Please check your internet connection and try again.'
      this.showSnack(msg, 'red darken-4')
    },
    showSnack(message, color = 'success', timeout = 4000) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('setSnackBarTimeOut', timeout)
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
/* ... (styles précédents) ... */

/* Nouveaux styles pour le popup amélioré */
.upcoming-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.upcoming-summary-banner {
  background: #f5f8ff;
  border-bottom: 1px solid #e0e0e0;
}

.upcoming-timeline {
  position: relative;
  max-height: 500px;
  overflow-y: auto;
}

.timeline-item {
  position: relative;
  padding-left: 30px;
}

.timeline-dot {
  position: absolute;
  left: 8px;
  top: 20px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  z-index: 2;
}

.dot-paid {
  background-color: #4caf50;
}

.dot-unpaid {
  background-color: #ff9800;
}

.timeline-line {
  position: absolute;
  left: 13px;
  top: 32px;
  bottom: 0;
  width: 2px;
  background-color: #e0e0e0;
}

.timeline-card {
  border-radius: 8px;
}

.paid-cycle {
  background: #f1f8f4;
}

/* Autres styles existants */
.summary-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #888;
  display: flex;
  align-items: center;
}

.summary-metric-card {
  background: #f8f8f8;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  height: 100%;
}

.summary-metric-label {
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
}

.summary-metric-value {
  font-size: 16px;
  font-weight: 600;
}

.summary-info-row {
  display: flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  border: 1px solid #e0e0e0;
}

.summary-banner {
  display: flex;
  align-items: flex-start;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-size: 13px;
}

.error-banner {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  color: #c62828;
}

.warning-banner {
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #f57f17;
}

.cycle-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

.cycle-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.cycle-card-title {
  font-size: 14px;
  font-weight: 600;
}

.cycle-period-tag {
  font-size: 11px;
  background: #eeeeee;
  padding: 2px 10px;
  border-radius: 20px;
}

.cycle-card-metrics {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 0.5rem 1rem;
  gap: 8px;
}

.cycle-metric-label {
  font-size: 11px;
  color: #999;
  margin-bottom: 3px;
}

.cycle-metric-value {
  font-size: 15px;
  font-weight: 600;
}

.cycle-action-card {
  cursor: pointer;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  transition: all 0.2s ease;
  background: #fff;
}

.cycle-action-card:hover {
  border-color: #1976d2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.cycle-action-card.paid-card {
  border-color: #4caf50;
  background: #f1f8f4;
  cursor: default;
}

.action-title {
  font-weight: 700;
  font-size: 1.1rem;
}

.action-subtitle {
  font-size: 0.85rem;
  color: #666;
  margin-top: 2px;
}

.amount-label {
  font-weight: 500;
  color: #333;
}
</style>
